import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { isNative } from './utils/platform';
import SplashScreen from './components/SplashScreen';
import usePageTitle from './hooks/usePageTitle';
import { titleForPath } from './config/navigation';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import DashboardLayout from './components/layout/DashboardLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import VerifyEmail from './pages/auth/VerifyEmail';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Dashboard from './pages/dashboard/Dashboard';
import Opportunities from './pages/dashboard/Opportunities';
import OpportunityDetail from './pages/dashboard/OpportunityDetail';
import WatchList from './pages/dashboard/WatchList';
import Pipeline from './pages/dashboard/Pipeline';
import PursuitWorkspace from './pages/dashboard/PursuitWorkspace';
import Agents from './pages/dashboard/Agents';
import Analytics from './pages/dashboard/Analytics';
import AIAssistant from './pages/dashboard/AIAssistant';
import ProposalWriter from './pages/dashboard/ProposalWriter';
import PastPerformance from './pages/dashboard/PastPerformance';
import Boilerplate from './pages/dashboard/Boilerplate';
import PricingProfile from './pages/dashboard/PricingProfile';
import CRM from './pages/dashboard/CRM';
import Documents from './pages/dashboard/Documents';
import Integrations from './pages/dashboard/Integrations';
import Team from './pages/dashboard/Team';
import Settings from './pages/dashboard/Settings';
import Billing from './pages/dashboard/Billing';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import About from './pages/About';
import ForAgents from './pages/ForAgents';
import OAuthAuthorize from './pages/OAuthAuthorize';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Keeps document.title in step with the route. Mounted once rather than
 * calling a hook in 25 page components.
 */
function RouteTitle() {
  const { pathname } = useLocation();
  usePageTitle(titleForPath(pathname));
  return null;
}

function AppRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return;

    // Handle deep links (Universal Links / App Links) from email/external sources
    let cleanup: (() => void) | undefined;

    import('@capacitor/app').then(({ App: CapApp }) => {
      CapApp.addListener('appUrlOpen', (data: { url: string }) => {
        try {
          const url = new URL(data.url);
          navigate(url.pathname + url.search);
        } catch {
          // ignore malformed URLs
        }
      }).then((listener) => {
        cleanup = () => listener.remove();
      });
    }).catch(() => {
      // @capacitor/app not yet installed — no-op
    });

    return () => { cleanup?.(); };
  }, [navigate]);

  return (
    <>
      <RouteTitle />
      <Routes>
      {/* Public Routes */}
      <Route element={<MainLayout />}>
        {/* On native, redirect the landing page directly to login */}
        <Route path="/" element={isNative() ? <Navigate to="/login" replace /> : <Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/about" element={<About />} />
        {/* /for-agents, not /agents. The dashboard registers /agents for the
            AI Actions page, and two routes at one path is not an error —
            React Router takes the first match, so this public page silently
            shadowed the signed-in one and "AI Actions" in the sidebar landed
            on marketing copy. */}
        <Route path="/for-agents" element={<ForAgents />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
      </Route>
        {/* OAuth consent — deliberately outside every layout. A consent
            screen with a nav bar invites someone to wander off mid-flow, and
            the client is left waiting on a callback that never arrives. */}
        <Route path="/oauth/authorize" element={<OAuthAuthorize />} />


        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Protected Dashboard Routes */}
        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/opportunities" element={<Opportunities />} />
          <Route path="/opportunities/:id" element={<OpportunityDetail />} />
          <Route path="/watchlist" element={<WatchList />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/pursuits" element={<Navigate to="/pipeline?view=list" replace />} />
          <Route path="/pursuits/:key" element={<PursuitWorkspace />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/ai-assistant" element={<AIAssistant />} />
          <Route path="/proposal-writer" element={<ProposalWriter />} />
          <Route path="/past-performance" element={<PastPerformance />} />
          <Route path="/boilerplate" element={<Boilerplate />} />
          <Route path="/pricing-profile" element={<PricingProfile />} />
          <Route path="/crm" element={<CRM />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/team" element={<Team />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/billing" element={<Billing />} />
        </Route>

        {/* Catch-all — anything unmatched renders a real page inside the
            public shell instead of a blank white screen. */}
        <Route element={<MainLayout />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(isNative());

  return (
    <ErrorBoundary>
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
